/**
 * A person's private, read-only calendar link for Google Calendar (Other
 * calendars → From URL): copy it, or reset it so the old one stops working.
 * The hub and the governance system both show it.
 */
import { Copy, RotateCcw } from "lucide-react";
import { useState } from "react";
import { ConfirmDialog } from "./overlay";
import { Button, ErrorNote, Field, Input, Notice, Skeleton } from "./ui";

export function CalendarLink({
  url,
  loading,
  error,
  unavailable,
  onReset,
  resetting,
  onCopied,
}: {
  url: string | null;
  loading: boolean;
  error: unknown;
  /** Shown when there's no link to give (no public address yet). */
  unavailable: string;
  /** Resolves once the new link is made. */
  onReset: () => Promise<unknown>;
  resetting: boolean;
  /** Told whether copying worked, to say so. */
  onCopied: (ok: boolean) => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const copy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      onCopied(true);
    } catch {
      onCopied(false);
    }
  };
  if (loading) return <Skeleton className="h-16" />;
  if (error) return <ErrorNote error={error} />;
  return (
    <>
      {url ? (
        <div className="space-y-3">
          <Field label="Your private calendar link" hint="In Google Calendar: Other calendars → From URL.">
            <div className="flex gap-2">
              <Input readOnly value={url} onFocus={(e) => e.currentTarget.select()} className="num min-w-0 font-mono text-[13px]" />
              <Button icon={<Copy className="size-4" />} onClick={copy}>
                Copy
              </Button>
            </div>
          </Field>
          <Notice tone="warn">Anyone with this link can see your calendar.</Notice>
          <Button variant="link" size="sm" icon={<RotateCcw className="size-3.5" />} onClick={() => setConfirming(true)}>
            Reset link
          </Button>
        </div>
      ) : (
        <Notice tone="info">{unavailable}</Notice>
      )}
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title="Reset your calendar link?"
        confirmLabel="Reset link"
        tone="danger"
        loading={resetting}
        onConfirm={() => void onReset().then(() => setConfirming(false), () => undefined)}
      >
        The current link stops working. Google Calendar needs the new one.
      </ConfirmDialog>
    </>
  );
}
